import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../utils/api-client';

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string): Promise<void> => {
        set({ isLoading: true, error: null });
        try {
          const data = await api.post<{
            user: User;
            tenant: Tenant;
            accessToken: string;
          }>('/auth/login', { email, password }, { skipAuth: true });

          set({
            user: data.user,
            tenant: data.tenant,
            accessToken: data.accessToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Login failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw err;
        }
      },

      register: async (email: string, password: string, name?: string): Promise<void> => {
        set({ isLoading: true, error: null });
        try {
          const data = await api.post<{
            user: User;
            tenant: Tenant;
            accessToken: string;
          }>('/auth/register', { email, password, name }, { skipAuth: true });

          set({
            user: data.user,
            tenant: data.tenant,
            accessToken: data.accessToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Registration failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw err;
        }
      },

      logout: async (): Promise<void> => {
        try {
          await api.post('/auth/logout', undefined, { timeout: 5000, retries: 1 });
        } catch {
          // Ignore logout errors - always clear local state
        }
        set({
          user: null,
          tenant: null,
          accessToken: null,
          isAuthenticated: false,
          error: null,
        });
      },

      refreshToken: async (): Promise<void> => {
        try {
          const data = await api.post<{ accessToken: string }>('/auth/refresh', undefined, {
            skipAuth: true,
            timeout: 10000,
            retries: 1,
          });
          set({ accessToken: data.accessToken });
        } catch {
          // If refresh fails, log out
          void get().logout();
        }
      },

      clearError: (): void => { set({ error: null }); },
    }),
    {
      name: 'profitos-auth',
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
